<template>
  <!-- CD-10: Profile page -->
  <div class="space-y-6 mx-w-[518px]">
    <div class="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
      <div>
        <h3 class="text-[14px] font-semibold mb-1 text-grey-light-700">Security</h3>
        <p class="text-[14px] text-grey-light-600">Update password.</p>
      </div>
      <div class="space-y-3">
        <div class="w-full md:max-w-[512px] space-y-3">
          <div>
            <LabelComponent for="password" :class="{ 'text-grey-light-400': !editingSecurity }"
              >New Password</LabelComponent
            >
            <Input
              id="password"
              type="password"
              placeholder="New Password"
              v-model="securityData.password"
              :disabled="!editingSecurity || isSaving"
              :class="[
                inputFocusStyle,
                {
                  'border-red-500': errors.password,
                  'border-green-500': securityData.password.length >= 8 && !errors.password,
                },
              ]"
            />
            <p v-if="errors.password" class="text-red-500 text-sm mt-1">{{ errors.password }}</p>
            <p v-else class="text-xs text-grey-light-400 mt-1">Password must be a minimum of 8 characters</p>
          </div>
          <div v-if="editingSecurity">
            <LabelComponent for="confirmPassword" :class="{ 'text-grey-light-400': !editingSecurity }"
              >Confirm New Password</LabelComponent
            >
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm New Password"
              v-model="securityData.confirmPassword"
              :disabled="!editingSecurity || isSaving"
              :class="[
                inputFocusStyle,
                {
                  'border-red-500':
                    errors.confirmPassword ||
                    (securityData.confirmPassword.length >= 8 &&
                      securityData.confirmPassword !== securityData.password),
                  'border-green-500':
                    securityData.confirmPassword === securityData.password &&
                    securityData.confirmPassword.length >= 8 &&
                    !errors.confirmPassword,
                },
              ]"
            />
            <p v-if="errors.confirmPassword" class="text-red-500 text-sm mt-1">{{ errors.confirmPassword }}</p>
            <p
              v-else-if="
                securityData.confirmPassword.length >= 8 && securityData.confirmPassword !== securityData.password
              "
              class="text-red-500 text-sm mt-1"
            >
              Passwords do not match
            </p>
          </div>
        </div>
        <div class="flex md:max-w-[512px]" :class="buttonClasses" v-if="!editingSecurity">
          <Button
            variant="link"
            class="text-brand-700 font-semibold px-0"
            :disabled="isSaving"
            @click="enableEdit('security')"
            data-testid="change-password"
            >Change Password</Button
          >
        </div>
        <div class="flex" :class="buttonClasses" v-else>
          <div class="flex items-center">
            <Button
              variant="link"
              class="w-auto px-0 md:w-auto mr-4"
              :disabled="isSaving"
              @click="handleCancel"
              data-testid="cancel-password"
              >Cancel</Button
            >
            <Button
              variant="link"
              class="text-brand-700 font-semibold px-0"
              :disabled="
                isSaving ||
                (editingSecurity &&
                  (securityData.password.length < 8 || securityData.password !== securityData.confirmPassword))
              "
              @click="handleSave()"
              data-testid="change-password"
            >
              <span v-if="isSaving" class="flex items-center gap-2">
                <LayupSpinner :size="4" container-classes="" />
                Changing...
              </span>
              <span v-else>Change Password</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
    <hr class="col-span-full mb-[50px]" />

    <div class="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
      <div>
        <h3 class="text-[14px] font-semibold mb-1 text-grey-light-700">Personal & Contact Details</h3>
        <p class="text-[14px] text-grey-light-600">Update personal details.</p>
      </div>
      <div class="space-y-4">
        <div class="w-full md:max-w-[512px] space-y-4">
          <div>
            <LabelComponent for="firstName" :class="{ 'text-grey-light-400': !editingPersonal }"
              >First Name</LabelComponent
            >
            <Input
              id="firstName"
              placeholder="Enter your full name"
              v-model="personalDetails.firstName"
              :disabled="!editingPersonal || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.firstName }]"
            />
            <p v-if="errors.firstName" class="text-red-500 text-sm mt-1">{{ errors.firstName }}</p>
          </div>
          <div>
            <LabelComponent for="surname" :class="{ 'text-grey-light-400': !editingPersonal }">Surname</LabelComponent>
            <Input
              id="surname"
              placeholder="Enter your surname"
              v-model="personalDetails.surname"
              :disabled="!editingPersonal || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.surname }]"
            />
            <p v-if="errors.surname" class="text-red-500 text-sm mt-1">{{ errors.surname }}</p>
          </div>
          <div>
            <Tabs v-model="identityTab" @update:model-value="onIdentityTabChange">
              <TabsList class="w-full justify-start bg-transparent p-0">
                <TabsTrigger value="id" :disabled="!editingPersonal || isSaving" class="text-left pl-0"
                  >ID Number</TabsTrigger
                >
                <TabsTrigger value="passport" :disabled="!editingPersonal || isSaving" class="text-left pl-0"
                  >Passport Number</TabsTrigger
                >
              </TabsList>
              <TabsContent value="id" class="mt-2">
                <Input
                  id="idNumber"
                  placeholder="ID number"
                  v-model="personalDetails.idNumber"
                  :disabled="!editingPersonal || isSaving"
                  :class="[inputFocusStyle, { 'border-red-500': errors.idNumber }]"
                />
                <p v-if="errors.idNumber" class="text-red-500 text-sm mt-1">{{ errors.idNumber }}</p>
              </TabsContent>
              <TabsContent value="passport" class="mt-2">
                <Input
                  id="passportNumber"
                  placeholder="Passport number"
                  v-model="personalDetails.passportNumber"
                  :disabled="!editingPersonal || isSaving"
                  :class="[inputFocusStyle, { 'border-red-500': errors.passportNumber }]"
                />
                <p v-if="errors.passportNumber" class="text-red-500 text-sm mt-1">{{ errors.passportNumber }}</p>
              </TabsContent>
            </Tabs>
          </div>
          <div>
            <LabelComponent for="email" :class="{ 'text-grey-light-400': !editingPersonal }">Email</LabelComponent>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              v-model="personalDetails.email"
              :disabled="!editingPersonal || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.email }]"
            />
            <p v-if="errors.email" class="text-red-500 text-sm mt-1">{{ errors.email }}</p>
          </div>
          <div>
            <LabelComponent for="phone" :class="{ 'text-grey-light-400': !editingPersonal }"
              >Phone number</LabelComponent
            >
            <div>
              <div class="flex gap-2">
                <Select v-model="personalDetails.countryCode" :disabled="!editingPersonal || isSaving">
                  <SelectTrigger class="w-24"><SelectValue placeholder="SA" /></SelectTrigger>
                  <SelectContent><SelectItem value="SA">SA</SelectItem></SelectContent>
                </Select>
                <Input
                  id="phone"
                  placeholder="Enter your phone number"
                  v-model="personalDetails.phone"
                  :disabled="!editingPersonal || isSaving"
                  :class="[inputFocusStyle, 'flex-1', { 'border-red-500': errors.phone }]"
                />
              </div>
              <p v-if="errors.phone" class="text-red-500 text-sm mt-1">{{ errors.phone }}</p>
            </div>
          </div>
        </div>
        <div class="flex md:max-w-[512px]" :class="isDesktop ? 'justify-end' : 'justify-start'" v-if="!editingPersonal">
          <Button
            variant="link"
            class="text-brand-700 font-semibold px-0"
            :disabled="isSaving"
            @click="enableEdit('personal')"
            data-testid="edit-personal"
            >Edit</Button
          >
        </div>
        <div class="flex" :class="isDesktop ? 'justify-end' : 'justify-start'" v-else>
          <div class="flex flex-col-reverse md:flex-row justify-end gap-3 w-full">
            <Button
              variant="outline"
              class="w-full md:w-auto"
              :disabled="isSaving"
              @click="handleCancel"
              data-testid="cancel-button"
              >Cancel</Button
            >
            <Button
              class="bg-brand-600 hover:bg-brand-700 w-full md:w-auto"
              :disabled="editingSecurity || isSaving || (!editingAddress && !editingPersonal)"
              data-testid="save-button"
              @click="handleSave"
            >
              <span v-if="isSaving" class="flex items-center gap-2">
                <LayupSpinner :size="4" container-classes="" />
                Saving...
              </span>
              <span v-else>Save</span>
            </Button>
          </div>
        </div>
      </div>
    </div>

    <hr class="col-span-full mb-[50px]" />

    <div class="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
      <div>
        <h3 class="text-[14px] font-semibold mb-1 text-grey-light-700">Address</h3>
        <p class="text-[14px] text-grey-light-600">Update address details.</p>
      </div>
      <div class="space-y-4">
        <div class="w-full md:max-w-[512px] space-y-4">
          <div>
            <LabelComponent for="country" :class="{ 'text-grey-light-400': !editingAddress }">Country</LabelComponent>
            <Input
              id="country"
              placeholder="Enter your country"
              v-model="address.country"
              :disabled="!editingAddress || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.country }]"
            />
            <p v-if="errors.country" class="text-red-500 text-sm mt-1">{{ errors.country }}</p>
          </div>
          <div>
            <LabelComponent for="province" :class="{ 'text-grey-light-400': !editingAddress }">Province</LabelComponent>
            <Input
              id="province"
              placeholder="Enter your province"
              v-model="address.province"
              :disabled="!editingAddress || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.province }]"
            />
            <p v-if="errors.province" class="text-red-500 text-sm mt-1">{{ errors.province }}</p>
          </div>
          <div>
            <LabelComponent for="city" :class="{ 'text-grey-light-400': !editingAddress }">City</LabelComponent>
            <Input
              id="city"
              placeholder="Enter your city"
              v-model="address.city"
              :disabled="!editingAddress || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.city }]"
            />
            <p v-if="errors.city" class="text-red-500 text-sm mt-1">{{ errors.city }}</p>
          </div>
          <div>
            <LabelComponent for="street" :class="{ 'text-grey-light-400': !editingAddress }"
              >Street Address</LabelComponent
            >
            <Input
              id="street"
              placeholder="Enter your street address"
              v-model="address.street"
              :disabled="!editingAddress || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.street }]"
            />
            <p v-if="errors.street" class="text-red-500 text-sm mt-1">{{ errors.street }}</p>
          </div>
          <div>
            <LabelComponent for="postalCode" :class="{ 'text-grey-light-400': !editingAddress }"
              >Postal Code</LabelComponent
            >
            <Input
              id="postalCode"
              placeholder="Enter your postal code"
              v-model="address.postalCode"
              :disabled="!editingAddress || isSaving"
              :class="[inputFocusStyle, { 'border-red-500': errors.postalCode }]"
            />
            <p v-if="errors.postalCode" class="text-red-500 text-sm mt-1">{{ errors.postalCode }}</p>
          </div>
        </div>
        <div class="flex md:max-w-[512px]" :class="isDesktop ? 'justify-end' : 'justify-start'" v-if="!editingAddress">
          <Button
            variant="link"
            class="text-brand-700 font-semibold px-0"
            :disabled="isSaving"
            @click="enableEdit('address')"
            data-testid="edit-address"
            >Edit</Button
          >
        </div>
        <div class="flex" :class="isDesktop ? 'justify-end' : 'justify-start'" v-else>
          <div class="flex flex-col-reverse md:flex-row justify-end gap-3 w-full">
            <Button
              variant="outline"
              class="w-full md:w-auto"
              :disabled="isSaving"
              @click="handleCancel"
              data-testid="cancel-button"
              >Cancel</Button
            >
            <Button
              class="bg-brand-600 hover:bg-brand-700 w-full md:w-auto"
              :disabled="editingSecurity || isSaving || (!editingAddress && !editingPersonal)"
              data-testid="save-button"
              @click="handleSave"
            >
              <span v-if="isSaving" class="flex items-center gap-2">
                <LayupSpinner :size="4" container-classes="" />
                Saving...
              </span>
              <span v-else>Save</span>
            </Button>
          </div>
        </div>
      </div>
    </div>

    <Dialog v-model:open="showUnsavedDialog">
      <DialogContent>
        <DialogHeader class="items-center">
          <WarningIcon />
          <DialogTitle class="mb-4">Unsaved Changes</DialogTitle>
          <DialogDescription
            >You have unsaved changes. Navigating away will discard all unsaved information.</DialogDescription
          >
        </DialogHeader>
        <hr />
        <DialogFooter>
          <Button variant="outline" @click="cancelDiscard">Cancel</Button>
          <Button class="bg-brand-600 hover:bg-brand-700 w-full sm:w-[100px] mb-4 sm:mb-0" @click="confirmDiscard">
            Discard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <SuccessErrorModal
      :show-modal="modal.show"
      :title="modal.title"
      :description="modal.description"
      :is-error="modal.isError"
      @close="modal.show = false"
    />
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SuccessErrorModal from '@/components/common/SuccessErrorModal.vue';
import WarningIcon from '@/components/icons/WarningIcon.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { LabelComponent } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NetworkError } from '@/errors';
import { updateUser } from '@/services/user';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { ModelAvsClientIdType, type UserServiceApiUserServiceUpdateRequest } from '@layup/api-sdk';

const inputFocusStyle =
  'border-[#D0D5DD] disabled:bg-grey-light-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus:border-brand-300 focus:shadow-[0_0_0_4px_rgba(15,125,136,0.24)]';

const userStore = useUserStore();
const { user } = storeToRefs(userStore);
const { isDesktop } = useBreakpoint();

const editingSecurity = ref(false);
const editingPersonal = ref(false);
const editingAddress = ref(false);
const showUnsavedDialog = ref(false);
const pendingSection = ref('');
const isSaving = ref(false);
const modal = ref({ show: false, title: '', description: '', isError: false });
const securityData = ref({ password: '', confirmPassword: '' });
const securityBackup = ref({ ...securityData.value });

const personalDetails = ref({
  firstName: '',
  surname: '',
  idNumber: '',
  passportNumber: '',
  email: '',
  countryCode: '',
  phone: '',
  usernameType: '',
});
const personalBackup = ref({ ...personalDetails.value });

const address = ref({ country: '', province: '', city: '', street: '', postalCode: '' });
const addressBackup = ref({ ...address.value });

const identityTab = ref('id');

const onIdentityTabChange = (value: string | number) => {
  if (value === 'id') personalDetails.value.passportNumber = '';
  else personalDetails.value.idNumber = '';
};

const errors = ref({
  password: '',
  confirmPassword: '',
  firstName: '',
  surname: '',
  idNumber: '',
  passportNumber: '',
  email: '',
  phone: '',
  country: '',
  province: '',
  city: '',
  street: '',
  postalCode: '',
});

const enableEdit = (section: string) => {
  if (editingSecurity.value || editingPersonal.value || editingAddress.value) {
    pendingSection.value = section;
    showUnsavedDialog.value = true;
    return;
  }

  activateEdit(section);
};

const activateEdit = (section: string) => {
  if (section === 'security') {
    securityBackup.value = { ...securityData.value };
    editingSecurity.value = true;
  } else if (section === 'personal') {
    personalBackup.value = { ...personalDetails.value };
    editingPersonal.value = true;
  } else if (section === 'address') {
    addressBackup.value = { ...address.value };
    editingAddress.value = true;
  }
};

const confirmDiscard = () => {
  handleCancel();
  showUnsavedDialog.value = false;
  activateEdit(pendingSection.value);
};

const cancelDiscard = () => {
  showUnsavedDialog.value = false;
  pendingSection.value = '';
};

const handleCancel = () => {
  clearErrors();

  if (editingSecurity.value) {
    securityData.value = { ...securityBackup.value };
    editingSecurity.value = false;
  } else if (editingPersonal.value) {
    personalDetails.value = { ...personalBackup.value };
    editingPersonal.value = false;
  } else if (editingAddress.value) {
    address.value = { ...addressBackup.value };
    editingAddress.value = false;
  }

  // Restore default user data
  init();
};

const buttonClasses = computed(() => (isDesktop.value ? 'justify-end' : 'justify-start'));

const submit = async (section: 'security' | 'personal' | 'address') => {
  try {
    const payload: UserServiceApiUserServiceUpdateRequest = { id: user.value._id || '', modelUserRequest: {} };

    if (section === 'security') {
      payload.modelUserRequest!.password = securityData.value.password;
    } else if (section === 'personal') {
      payload.modelUserRequest!.name = `${personalDetails.value.firstName} ${personalDetails.value.surname}`;
      if (identityTab.value === 'id') {
        payload.modelUserRequest!.identificationType = ModelAvsClientIdType.identityNumber;
      } else {
        payload.modelUserRequest!.identificationType = ModelAvsClientIdType.passportNumber;
      }
      payload.modelUserRequest!.idNumber = personalDetails.value.idNumber;
      payload.modelUserRequest!.passportNumber = personalDetails.value.passportNumber;
      payload.modelUserRequest!.email = personalDetails.value.email;
      payload.modelUserRequest!.cellNumber = personalDetails.value.phone;
    } else if (section === 'address') {
      payload.modelUserRequest!.address = `${address.value.street}, ${address.value.city}, ${address.value.province}, ${address.value.country}, ${address.value.postalCode}`;
    }

    const updatedUser = await updateUser(payload);

    if (updatedUser) userStore.user = updatedUser;

    const messages = {
      security: 'Your password has been updated successfully.',
      personal: 'Your personal details have been updated successfully.',
      address: 'Your address has been updated successfully.',
    };

    modal.value = {
      show: true,
      title: 'Success',
      description: messages[section],
      isError: false,
    };

    addMetric(`update_profile`, { status: 'success', channel: 'shopper', section });
  } catch (error: any) {
    modal.value = {
      show: true,
      title: 'Error',
      description: 'Failed to update profile. Please try again.',
      isError: true,
    };

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`update_profile`, { status: 'network_error', channel: 'shopper', section });
    } else {
      createException(error);
      addMetric(`update_profile`, { status: 'error', channel: 'shopper', section });
    }
  }
};

const clearErrors = () =>
  Object.keys(errors.value).forEach((key) => {
    errors.value[key as keyof typeof errors.value] = '';
  });

const validateSecurity = (): boolean => {
  clearErrors();

  let isValid = true;

  if (!securityData.value.password) {
    errors.value.password = 'Password is required';
    isValid = false;
  } else if (securityData.value.password.length < 8) {
    errors.value.password = 'Password must be at least 8 characters';
    isValid = false;
  }

  if (!securityData.value.confirmPassword) {
    errors.value.confirmPassword = 'Please confirm your password';
    isValid = false;
  } else if (securityData.value.password !== securityData.value.confirmPassword) {
    errors.value.confirmPassword = 'Passwords do not match';
    isValid = false;
  }

  return isValid;
};

const validatePersonal = (): boolean => {
  clearErrors();

  let isValid = true;

  if (!personalDetails.value.firstName) {
    errors.value.firstName = 'Full name is required';
    isValid = false;
  }

  if (!personalDetails.value.surname) {
    errors.value.surname = 'Surname is required';
    isValid = false;
  }

  if (identityTab.value === 'id') {
    if (!personalDetails.value.idNumber) {
      errors.value.idNumber = 'ID number is required';
      isValid = false;
    } else if (!/^[0-9]{13}$/.test(personalDetails.value.idNumber)) {
      errors.value.idNumber = 'ID number must be 13 digits';
      isValid = false;
    }
    personalDetails.value.passportNumber = '';
  } else {
    if (!personalDetails.value.passportNumber) {
      errors.value.passportNumber = 'Passport number is required';
      isValid = false;
    }
    personalDetails.value.idNumber = '';
  }

  if (!personalDetails.value.email) {
    errors.value.email = 'Email is required';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalDetails.value.email)) {
    errors.value.email = 'Please enter a valid email address';
    isValid = false;
  }

  if (!personalDetails.value.phone) {
    errors.value.phone = 'Phone number is required';
    isValid = false;
  } else if (!/^(0[0-9]{9}|\+27[0-9]{9})$/.test(personalDetails.value.phone)) {
    errors.value.phone = 'Phone number must be 10 digits';
    isValid = false;
  }

  return isValid;
};

const validateAddress = (): boolean => {
  clearErrors();
  let isValid = true;
  if (!address.value.country) {
    errors.value.country = 'Country is required';
    isValid = false;
  }

  if (!address.value.province) {
    errors.value.province = 'Province is required';
    isValid = false;
  }

  if (!address.value.city) {
    errors.value.city = 'City is required';
    isValid = false;
  }

  if (!address.value.street) {
    errors.value.street = 'Street address is required';
    isValid = false;
  }

  if (!address.value.postalCode) {
    errors.value.postalCode = 'Postal code is required';
    isValid = false;
  }

  return isValid;
};

const handleSave = async () => {
  isSaving.value = true;
  try {
    if (editingSecurity.value) {
      if (!validateSecurity()) return;

      await submit('security');

      editingSecurity.value = false;
    } else if (editingPersonal.value) {
      if (!validatePersonal()) return;

      await submit('personal');

      editingPersonal.value = false;
    } else if (editingAddress.value) {
      if (!validateAddress()) return;

      await submit('address');

      editingAddress.value = false;
    }
  } finally {
    isSaving.value = false;
  }
};

const init = () => {
  if (user.value) {
    personalDetails.value.email = user.value.email || '';
    personalDetails.value.idNumber = user.value.idNumber || '';
    personalDetails.value.passportNumber = user.value.passportNumber || '';
    identityTab.value = personalDetails.value.passportNumber ? 'passport' : 'id';
    personalDetails.value.phone = user.value.cellNumber || '';
    if (user.value.name) {
      const [firstName, surname] = user.value.name.split(' ');
      personalDetails.value.firstName = firstName;
      personalDetails.value.surname = surname;
    }
    if (user.value.address) {
      const addressParts = user.value.address.split(',').map((part) => part.trim());
      address.value.street = addressParts[0] || '';
      address.value.city = addressParts[1] || '';
      address.value.province = addressParts[2] || '';
      address.value.country = addressParts[3] || '';
      address.value.postalCode = addressParts[4] || '';
    }
  }
};

init();
</script>
